import { Badge } from "@/components/ui/badge";
import { fmtNum } from "@/lib/format";

const META = {
  holding: { label: "HOLDING", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", bar: "bg-emerald-500" },
  trimmed: { label: "TRIMMED", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", bar: "bg-amber-400" },
  dumped: { label: "DUMPED", cls: "bg-red-500/15 text-red-300 border-red-500/40", bar: "bg-red-500" },
  linked_holding: { label: "LINKED · HOLDING", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", bar: "bg-emerald-500" },
  linked_trimmed: { label: "LINKED · TRIMMED", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", bar: "bg-amber-400" },
  linked_dumped: { label: "LINKED · DUMPED", cls: "bg-red-500/15 text-red-300 border-red-500/40", bar: "bg-red-500" },
  never_held: { label: "NEVER HELD", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40", bar: "bg-slate-500" },
  unknown: { label: "UNKNOWN", cls: "bg-slate-500/15 text-slate-400 border-slate-500/40", bar: "bg-slate-600" },
  unknown_history: { label: "NO HISTORY", cls: "bg-slate-500/15 text-slate-400 border-slate-500/40", bar: "bg-slate-600" },
};

const pct = (v) => (v == null ? "N/A" : `${v}%`);
const ago = (ts) => { if (!ts) return null; const h = Math.round((Date.now() / 1000 - ts) / 3600); return h < 1 ? "just now" : h < 24 ? `${h}h ago` : `${Math.round(h / 24)}d ago`; };

export function DevSoldPanel({ ds, chain }) {
  if (!ds) {
    return (
      <div className="mt-3 pt-3 border-t border-[#222F43]" data-testid="dev-sold">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Dev sold since launch?</div>
        <p className="text-[11px] font-mono text-slate-600" data-testid="dev-sold-na">{chain === "solana" ? "Unavailable — creator wallet not identified" : "Sell history not available on this chain yet"}</p>
      </div>
    );
  }
  const m = META[ds.status] || META.unknown;
  const linked = ds.status.startsWith("linked_") ? ds.linked : null;
  const soldPct = linked ? linked.soldPct : ds.soldPct;
  const evm = ds.source === "goplus";

  return (
    <div className="mt-3 pt-3 border-t border-[#222F43]" data-testid="dev-sold">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Dev sold since launch?</div>
        <Badge className={`${m.cls} border text-[10px] font-display font-extrabold px-2 py-0 rounded-full`} data-testid="dev-sold-status">{m.label}</Badge>
      </div>

      {!evm && soldPct != null && !["never_held", "unknown"].includes(ds.status) && (
        <div className="flex items-center gap-2 mb-2" data-testid="dev-sold-bar">
          <div className="flex-1 h-1.5 rounded-full bg-[#0d1420] overflow-hidden">
            <div className={`h-full ${m.bar}`} style={{ width: `${Math.min(100, soldPct)}%` }} />
          </div>
          <span className="font-mono text-xs text-slate-200 w-20 text-right">{soldPct}% sold</span>
        </div>
      )}

      <div className="text-[11px] font-mono text-slate-400 space-y-0.5">
        {evm && (
          <>
            <div data-testid="dev-sold-detail">Creator currently holds <span className="text-slate-200">{pct(ds.balancePctSupply)}</span> of supply</div>
            <div className="text-slate-600">Sell history isn't available on EVM chains yet — current holding via GoPlus.</div>
          </>
        )}
        {!evm && ds.status === "never_held" && <div data-testid="dev-sold-detail">Dev wallet never held any tokens — no dev buy at launch (could be using other wallets).</div>}
        {!evm && ds.status === "unknown" && <div data-testid="dev-sold-detail">Creator wallet history is deeper than we scan (older/busier wallet). Holds {pct(ds.balancePctSupply)} of supply now.</div>}
        {!evm && linked && (
          <div data-testid="dev-sold-detail">
            Dev wallet never held directly, but paid fees for <span className="text-slate-200">{linked.wallets}</span> linked wallet{linked.wallets === 1 ? "" : "s"} that bought <span className="text-slate-200">{pct(linked.receivedPctSupply)}</span> of supply · <span className="text-slate-200">{linked.soldPct}%</span> of that sold
          </div>
        )}
        {!evm && ["holding", "trimmed", "dumped"].includes(ds.status) && (
          <div data-testid="dev-sold-detail">
            Bag at launch <span className="text-slate-200">{pct(ds.receivedPctSupply)}</span> of supply · sold {fmtNum(Math.round(ds.sold))} in {ds.sellTxs} sell{ds.sellTxs === 1 ? "" : "s"}
            {ds.movedOut > 0 ? ` · moved ${fmtNum(Math.round(ds.movedOut))} to other wallets` : ""} · holds <span className="text-slate-200">{pct(ds.balancePctSupply)}</span> now
            {ds.lastOutAt ? <span className="text-slate-600"> · last sell {ago(ds.lastOutAt)}</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}
